/**
 * share.js — URL sharing via compiler-driven token substitution + Brotli.
 *
 * Compression (with compiler leaves):
 *   1. Walk the parse tree's leaves to identify exact token boundaries
 *   2. Replace known tokens with 2-byte escape sequences (0x01 + index)
 *   3. Brotli quality-11 compression
 *   4. Base64url encoding
 *
 * Decompression (no compiler needed):
 *   Reverse: base64url → Brotli → scan for 0x01 escapes → expand tokens
 *
 * The escape-byte scheme (0x01 = SOH control char) is fully UTF-8 safe.
 * Dictionary trained from all Sonic Pi examples + help snippets.
 */

import brotliPromise from 'brotli-wasm';

// ── Escape byte ──
// 0x01 (SOH) never appears in source text.  Tokens are encoded as:
//   0x01 <index>    where index is 0x00–0xEC (237 tokens)
// A literal 0x01 in source (essentially impossible) is escaped as 0x01 0xFE.
const ESC = 0x01;
const ESC_LITERAL = 0xFE; // 0x01 0xFE = literal 0x01 byte

// ── Token dictionary ──
// Trained from 34 Sonic Pi examples + 114 help snippets + DEFAULT_SOURCE.
// Ordered by (frequency × length) — highest-value tokens first.

const TOKENS = [
  // -- compound keywords (save 10–18 bytes each) --
  'play_pattern_timed',
  'use_random_seed',
  'with_synth_defaults',
  'use_sample_defaults',
  'with_sample_defaults',
  'use_synth_defaults',
  'use_random_source',
  'use_transpose',
  'use_octave',
  'use_debug',
  'live_loop',
  'in_thread',
  'use_synth',
  'with_fx',
  'use_bpm',
  'use_fx',
  'with_synth',
  'control',
  // -- keywords / builtins --
  'shuffle',
  'define',
  'sample',
  'choose',
  'synth',
  'sleep',
  'chord',
  'scale',
  'range',
  'print',
  'times',
  'rrand',
  'play',
  'stop',
  'sync',
  'rand',
  'dice',
  'note',
  'tick',
  'look',
  'ring',
  'puts',
  'else',
  'cue',
  'set',
  'get',
  'end',
  'rrand_i',
  'rand_i',
  // -- parameters --
  'sustain_level:',
  'attack_level:',
  'cutoff_slide:',
  'beat_stretch:',
  'mod_range:',
  'auto_cue:',
  'num_octaves:',
  'mod_phase:',
  'note_slide:',
  'pulse_width:',
  'mod_rate:',
  'mod_invert_wave:',
  'invert_wave:',
  'pan_slide:',
  'cutoff_max:',
  'amp_slide:',
  'release:',
  'sustain:',
  'divisor:',
  'attack:',
  'cutoff:',
  'detune:',
  'finish:',
  'depth:',
  'decay:',
  'phase:',
  'steps:',
  'delay:',
  'start:',
  'wave:',
  'distort:',
  'reps:',
  'octave:',
  'name:',
  'pre_mix:',
  'pre_amp:',
  'slide:',
  'rate:',
  'note:',
  'room:',
  'amp:',
  'pan:',
  'mix:',
  'res:',
  'lpf:',
  'hpf:',
  // -- synths --
  ':dark_ambience',
  ':organ_tonewheel',
  ':bass_foundation',
  ':bass_highend',
  ':pretty_bell',
  ':mod_square',
  ':mod_pulse',
  ':tech_saws',
  ':supersaw',
  ':chiplead',
  ':subpulse',
  ':mod_damp',
  ':mod_sine',
  ':mod_tri',
  ':prophet',
  ':tb303',
  ':hoover',
  ':hollow',
  ':blade',
  ':growl',
  ':pluck',
  ':dsaw',
  ':beep',
  ':saw',
  ':square',
  ':pulse',
  ':noise',
  ':tri',
  ':fm',
  ':mod_dsaw',
  ':mod_saw',
  ':zawa',
  ':bnoise',
  ':rodeo',
  ':rhodey',
  ':dull_bell',
  ':kalimba',
  ':pnoise',
  ':gnoise',
  ':cnoise',
  ':chipbass',
  ':chipnoise',
  ':gabberkick',
  ':dpulse',
  ':dtri',
  ':mod_fm',
  ':mod_beep',
  // -- FX --
  ':reverb',
  ':echo',
  ':compressor',
  ':slicer',
  ':bitcrusher',
  ':krush',
  ':distortion',
  ':wobble',
  ':panslicer',
  ':rlpf',
  ':rhpf',
  ':flanger',
  ':ring_mod',
  ':pitch_shift',
  ':ixi_techno',
  ':whammy',
  ':tanh',
  ':gverb',
  ':level',
  ':mono',
  // -- sample prefixes --
  ':glitch_',
  ':vinyl_',
  ':elec_',
  ':ambi_',
  ':bass_',
  ':drum_',
  ':loop_',
  ':perc_',
  ':misc_',
  ':guit_',
  ':hat_',
  ':bd_',
  ':sn_',
  // -- full sample names --
  ':drum_heavy_kick',
  ':drum_snare_hard',
  ':drum_snare_soft',
  ':drum_cymbal_closed',
  ':bd_haus',
  ':bd_fat',
  ':bd_tek',
  ':sn_dub',
  ':sn_dolf',
  ':ambi_lunar_land',
  ':ambi_haunted_hum',
  ':ambi_piano',
  ':ambi_drone',
  ':ambi_choir',
  ':loop_amen',
  ':loop_industrial',
  ':loop_compus',
  ':loop_garzul',
  ':loop_mika',
  ':vinyl_hiss',
  ':vinyl_scratch',
  ':perc_bell',
  ':hat_zap',
  ':elec_twip',
  ':elec_blip',
  ':elec_plip',
  ':glitch_perc1',
  ':misc_cineboom',
  ':guit_em9',
  ':bass_voxy_c',
  ':bass_trance_c',
  ':bass_dnb_f',
  ':bass_hit_c',
  // -- SC808 --
  ':sc808_bassdrum',
  ':sc808_snare',
  ':sc808_clap',
  ':sc808_open_hihat',
  ':sc808_closed_hihat',
  ':sc808_cowbell',
  ':sc808_cymbal',
  // -- notes --
  ':e1',
  ':e2',
  ':e3',
  ':c1',
  ':c2',
  ':c4',
  ':b1',
  ':b2',
  ':b3',
  // -- scales / chords --
  ':minor',
  ':major',
  ':minor_pentatonic',
  ':maj9',
  // -- common numbers --
  '0.125',
  '0.25',
  '0.5',
  '0.1',
  '0.2',
  '0.3',
  '0.8',
  '1.5',
  '100',
  '130',
  // -- common patterns --
  ', ',
];

// Build lookup: token string → index,  index → token string
const TOKEN_TO_IDX = new Map();
TOKENS.forEach((tok, i) => TOKEN_TO_IDX.set(tok, i));

// Sort longest-first for greedy matching (used by both compiler and fallback paths)
const SORTED_TOKENS = TOKENS
  .map((tok, i) => ({ tok, idx: i, len: tok.length }))
  .sort((a, b) => b.len - a.len);

// ── Compiler-driven compression ──
// Uses leaves for precise token boundaries when available.

function compressBytes(source, leaves) {
  const out = [];

  if (leaves && leaves.length > 0) {
    // Primary path: compiler leaves give exact token spans
    let pos = 0;
    for (let i = 0; i < leaves.length; i++) {
      const lf = leaves[i];
      if (lf.s < pos || lf.s > source.length || lf.e > source.length) continue;

      // Copy gap (whitespace etc.) as raw UTF-8, escaping any 0x01 bytes
      pushRawUTF8(out, source, pos, lf.s);

      // Look up the token text in the dictionary
      const text = source.slice(lf.s, lf.e);
      const idx = TOKEN_TO_IDX.get(text);
      if (idx !== undefined) {
        out.push(ESC, idx);
      } else {
        pushRawUTF8(out, source, lf.s, lf.e);
      }
      pos = lf.e;
    }
    // Trailing content
    pushRawUTF8(out, source, pos, source.length);
  } else {
    // Fallback: greedy longest-match text scan (no compiler)
    let i = 0;
    outer: while (i < source.length) {
      for (const { tok, idx, len } of SORTED_TOKENS) {
        if (source.startsWith(tok, i)) {
          out.push(ESC, idx);
          i += len;
          continue outer;
        }
      }
      // Single character — raw UTF-8
      const cp = source.codePointAt(i);
      const ch = String.fromCodePoint(cp);
      pushRawChar(out, ch);
      i += ch.length;
    }
  }

  return new Uint8Array(out);
}

// Push a substring's UTF-8 bytes, escaping any 0x01 bytes
function pushRawUTF8(out, str, from, to) {
  if (from >= to) return;
  const sub = str.slice(from, to);
  const bytes = new TextEncoder().encode(sub);
  for (let i = 0; i < bytes.length; i++) {
    if (bytes[i] === ESC) { out.push(ESC, ESC_LITERAL); }
    else out.push(bytes[i]);
  }
}

// Push a single character's UTF-8 bytes, escaping 0x01
function pushRawChar(out, ch) {
  const bytes = new TextEncoder().encode(ch);
  for (let i = 0; i < bytes.length; i++) {
    if (bytes[i] === ESC) { out.push(ESC, ESC_LITERAL); }
    else out.push(bytes[i]);
  }
}

// ── Decompression (no compiler needed) ──

function decompressBytes(bytes) {
  const chunks = [];
  let i = 0;
  while (i < bytes.length) {
    if (bytes[i] === ESC && i + 1 < bytes.length) {
      const next = bytes[i + 1];
      if (next === ESC_LITERAL) {
        // Escaped literal 0x01
        chunks.push(ESC);
        i += 2;
      } else if (next < TOKENS.length) {
        // Token code
        chunks.push(TOKENS[next]);
        i += 2;
      } else {
        // Unknown escape — pass through
        chunks.push(bytes[i]);
        i++;
      }
    } else {
      chunks.push(bytes[i]);
      i++;
    }
  }

  // Build output: mix of strings (tokens) and raw bytes
  const parts = [];
  let rawStart = -1;
  for (let j = 0; j < chunks.length; j++) {
    if (typeof chunks[j] === 'string') {
      if (rawStart >= 0) {
        parts.push(decodeRawRun(chunks, rawStart, j));
        rawStart = -1;
      }
      parts.push(chunks[j]);
    } else {
      if (rawStart < 0) rawStart = j;
    }
  }
  if (rawStart >= 0) {
    parts.push(decodeRawRun(chunks, rawStart, chunks.length));
  }
  return parts.join('');
}

function decodeRawRun(chunks, from, to) {
  const bytes = new Uint8Array(to - from);
  for (let i = from; i < to; i++) bytes[i - from] = chunks[i];
  return new TextDecoder().decode(bytes);
}

// ── Base64url ──

function base64urlEncode(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64urlDecode(str) {
  str = str.replace(/-/g, '+').replace(/_/g, '/');
  while (str.length % 4) str += '=';
  const binary = atob(str);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// ── Brotli singleton ──

let brotli = null;

async function getBrotli() {
  if (!brotli) brotli = await brotliPromise;
  return brotli;
}

// ── Public API ──

export async function compressToHash(source, leaves) {
  const br = await getBrotli();

  // Layer 1: token substitution (compiler-driven or fallback)
  const substituted = compressBytes(source, leaves);

  // Layer 2: Brotli compress at max quality
  const compressed = br.compress(substituted, { quality: 11 });

  // Layer 3: base64url
  return base64urlEncode(compressed);
}

export async function decompressFromHash(hash) {
  const br = await getBrotli();

  // Layer 3: base64url decode
  const compressed = base64urlDecode(hash);

  // Layer 2: Brotli decompress
  const bytes = br.decompress(compressed);

  // Layer 1: expand token codes
  return decompressBytes(bytes);
}
